'use client';

import React, { useState, useRef, useLayoutEffect } from 'react';
import { DocumentArrowUpIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { RutgersCourse, UserAPCredit } from '@/app/types/interfaces';
import { getAllAPExams } from '@/app/lib/apCreditData';
import { getRutgersCoursesByBaseIds } from '@/app/services/userAcademicData';
import { parseTranscriptText, type TranscriptEarnedCourse } from '@/app/lib/transcriptParser';

let pdfJsPromise: Promise<typeof import('pdfjs-dist')> | null = null;

function loadPdfJs() {
  pdfJsPromise ??= import('pdfjs-dist').then((pdfjs) => {
    pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
    return pdfjs;
  });
  return pdfJsPromise;
}

interface TranscriptUploadProps {
  onExtractCourses: (courses: RutgersCourse[], earned?: TranscriptEarnedCourse[]) => void | boolean | Promise<void | boolean>;
  onExtractAPCredits: (apCredits: UserAPCredit[]) => void | boolean | Promise<void | boolean>;
  existingCourses: RutgersCourse[];
}

export default function TranscriptUpload({ 
  onExtractCourses, 
  onExtractAPCredits,
  existingCourses 
}: TranscriptUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [earnedRows, setEarnedRows] = useState<TranscriptEarnedCourse[]>([]);
  const [extractedCourses, setExtractedCourses] = useState<RutgersCourse[]>([]);
  const [extractedAPCredits, setExtractedAPCredits] = useState<UserAPCredit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef<object | null>(null);
  const reviewIdentityRef = useRef<object>({});
  const renderedReviewIdentity = reviewIdentityRef.current;
  const pendingSaveRef = useRef<object | null>(null);
  const [isSavingReview, setIsSavingReview] = useState(false);
  useLayoutEffect(() => {
    mountedRef.current = {};
    return () => { mountedRef.current = null; };
  }, []);

  const [warnings, setWarnings] = useState<string[]>([]);
  const [hasProcessedFile, setHasProcessedFile] = useState(false);

  const retireReview = () => {
    reviewIdentityRef.current = {};
    setError(null);
  };

  const clearReview = () => {
    retireReview();
    setExtractedCourses([]);
    setEarnedRows([]);
    setExtractedAPCredits([]);
    setWarnings([]);
    setHasProcessedFile(false);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // A local review edit does not cancel an already-issued persistence chain.
    if (!mountedRef.current || pendingSaveRef.current || reviewIdentityRef.current !== renderedReviewIdentity) return;
    const file = event.target.files?.[0];
    if (!file) return;
    clearReview();
    setError(null);

    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfjsLib = await loadPdfJs();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      setIsProcessing(true);

      try {
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          let pageText = '';
          let lastY: number | undefined;
          for (const item of textContent.items) {
            if (!('str' in item)) continue;
            const currentY = item.transform[5];
            if (lastY !== undefined && Math.abs(currentY - lastY) > 5) pageText += '\n';
            pageText += `${item.str} `;
            if (item.hasEOL) pageText += '\n';
            lastY = currentY;
          }
          fullText += `${pageText}\n\n`;
          setUploadProgress((i / pdf.numPages) * 100);
        }
      } finally {
        await pdf.destroy();
      }

      const parsed = parseTranscriptText(fullText, existingCourses.map(course => course.baseId), getAllAPExams());
      const earnedById = new Map(parsed.earnedCourses.map(row => [row.baseId, row]));
      const ids = [...earnedById.keys()];
      const resolved = ids.length
        ? await getRutgersCoursesByBaseIds(ids)
        : [];
      // Keep the resolver boundary fail-closed and deduplicated as well.
      const eligibleIds = new Set(ids);
      const resolvedById = new Map(resolved.filter(course => eligibleIds.has(course.baseId)).map(course => [course.baseId, course]));
      const unresolvedCount = ids.length - resolvedById.size;
      setEarnedRows(parsed.earnedCourses);
      setExtractedCourses([...resolvedById.values()].map(course => ({ ...course, credits: earnedById.get(course.baseId)!.credits })));
      setExtractedAPCredits(parsed.apCredits);
      const reviewWarnings = [...parsed.warnings, ...parsed.earnedCourses.map(row => `${row.baseId}: earned grade ${row.grade}, ${row.credits} credits${existingCourses.some(course => course.baseId.replace(/[:\-]/g, '') === row.baseId) ? '; existing course metadata requires reconciliation' : ''}.`)];
      setWarnings(unresolvedCount > 0 ? [...reviewWarnings, `${unresolvedCount} earned course(s) could not be matched to the catalog. Review and add them manually.`] : reviewWarnings);
      setHasProcessedFile(true);
    } catch {
      clearReview();
      setError('Failed to process PDF. Please try again.');
    } finally {
      setIsProcessing(false);
      setIsUploading(false);
      setUploadProgress(0);
      // Allow selecting the same file again after an error or a review.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddCourses = async () => {
    if (!mountedRef.current || pendingSaveRef.current || isUploading || isProcessing || reviewIdentityRef.current !== renderedReviewIdentity) return;
    const mounted = mountedRef.current;
    const review = renderedReviewIdentity;
    const pending = {};
    const current = () => mountedRef.current === mounted && reviewIdentityRef.current === review;
    // Keep physical pending separate from the editable review lifetime.
    pendingSaveRef.current = pending;
    setIsSavingReview(true);
    setIsProcessing(true); setError(null);
    try {
      const selected = new Set(extractedCourses.map(course => course.baseId));
      const selectedEarned = earnedRows.filter(row => selected.has(row.baseId));
      const coursesSaved = !extractedCourses.length || await onExtractCourses(extractedCourses, selectedEarned);
      if (!current()) return;
      if (coursesSaved === false) {
        setError('Transcript changes need review. Review the existing grades or reload the academic record; this transcript review is retained.');
        return;
      }
      if (extractedAPCredits.length) {
        const apSaved = await onExtractAPCredits(extractedAPCredits);
        if (!current()) return;
        if (apSaved === false) {
          setError('Some transcript changes need review. Review the saved academic record before trying again; this transcript review is retained.');
          return;
        }
      }
      clearReview();
    } catch {
      if (current()) setError('Transcript changes could not be confirmed. Review the saved academic record before trying again; this transcript review is retained.');
    } finally {
      if (pendingSaveRef.current === pending) {
        pendingSaveRef.current = null;
        if (mountedRef.current) { setIsProcessing(false); setIsSavingReview(false); }
      }
    }
  };

  const handleClearExtractions = () => {
    clearReview();
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-control p-6 shadow-e1">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Upload Transcript
        </h3>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || isProcessing}
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-control hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
          Upload PDF
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileSelect}
        className="hidden"
      />
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
        Upload a text-based PDF, then review confirmed earned-course and AP-score matches before adding them.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-control">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {isUploading && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600 dark:text-slate-400">Processing PDF...</span>
            <span className="text-sm text-slate-600 dark:text-slate-400">{Math.round(uploadProgress)}%</span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
            <div 
              className="bg-red-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {isProcessing && (
        <div className="mb-4 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{isSavingReview ? 'Saving transcript changes…' : 'Analyzing transcript...'}</p>
          {isSavingReview && <p className="text-sm text-slate-600 dark:text-slate-400">Already-started changes may finish.</p>}
        </div>
      )}

      {hasProcessedFile && (
        <div className="mt-6 space-y-4">
          {warnings.length > 0 && (
            <div role="status" className="rounded-control p-4 bg-[var(--ss-warning-bg)] text-[var(--ss-warning)]">
              <h4 className="font-semibold mb-2">Review skipped records</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                {warnings.map(warning => <li key={warning}>{warning}</li>)}
              </ul>
            </div>
          )}
          {extractedCourses.length === 0 && extractedAPCredits.length === 0 && (
            <p className="text-sm text-slate-600 dark:text-slate-400">No new confirmed matches to add. You can add missing courses or AP scores manually.</p>
          )}
          {extractedCourses.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-control p-4">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Found {extractedCourses.length} course(s)
              </h4>
              <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                {extractedCourses.map((course, idx) => (
                  <div key={`${course.baseId}-${idx}`} className="flex items-center justify-between text-sm text-blue-800 dark:text-blue-200 bg-white dark:bg-slate-800 rounded px-3 py-2">
                    <span>
                      {course.department}:{course.class_number} - {course.courseName}
                    </span>
                    <button
                      onClick={() => {
                        retireReview();
                        setExtractedCourses(prev => prev.filter((_, i) => i !== idx));
                      }}
                      className="ml-3 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                      aria-label={`Remove ${course.department}:${course.class_number}`}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {extractedAPCredits.length > 0 && (
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-control p-4">
              <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">
                Found {extractedAPCredits.length} AP credit(s)
              </h4>
              <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                {extractedAPCredits.map((ap, idx) => (
                  <div key={`${ap.examId}-${ap.score}-${idx}`} className="flex items-center justify-between text-sm text-purple-800 dark:text-purple-200 bg-white dark:bg-slate-800 rounded px-3 py-2">
                    <span>
                      {ap.examName} - Score: {ap.score}/5
                    </span>
                    <button
                      onClick={() => {
                        retireReview();
                        setExtractedAPCredits(prev => prev.filter((_, i) => i !== idx));
                      }}
                      className="ml-3 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                      aria-label={`Remove ${ap.examName}`}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleAddCourses}
              disabled={isUploading || isProcessing || (extractedCourses.length === 0 && extractedAPCredits.length === 0)}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-control hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              Add to My Courses
            </button>
            <button
              onClick={handleClearExtractions}
              aria-label="Clear transcript review"
              className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-control hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

